import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { collection, query, getDocs, getCountFromServer } from "firebase/firestore";
import { db } from "../../config/firebase";
import {
  buildCountQueryConstraints,
  buildQueryConstraints,
  filterCalendarsClientSide,
  MAX_CALENDARS_LIMIT,
} from "./utils";
import { invalidateQuery } from "../utils";
import Calendar from "../../models/calendars/Calendar";

const QUERY_KEY = "calendars-table";

/**
 * Hook for paginated calendars in tables.
 * - Pagination support via useInfiniteQuery
 * - Includes total count before paginating
 * - Client-side filtering applied via 'search' in filters
 */
export function useCalendarsForTables({ filters = {}, enabled = true }) {
  const { search, ...serverFilters } = filters;

  return useInfiniteQuery({
    queryKey: [QUERY_KEY, JSON.stringify(serverFilters)],
    keepPreviousData: true,
    enabled,
    queryFn: ({ pageParam }) => fetchCalendarsPage({ lastDoc: pageParam, filters: serverFilters }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.lastDoc : undefined),
    select: (data) => {
      const fetched = data?.pages?.flatMap((page) => page.calendars) || [];
      const fetchCount = fetched.length;
      const totalCount = data.pages[0]?.totalCount ?? 0;
      const calendars = filterCalendarsClientSide({ calendars: fetched, search });

      return { ...data, calendars, totalCount, fetchCount, filteredCount: calendars.length };
    },
  });
}

/**
 * Fetch one page of calendars and (on first page) the total count.
 */
async function fetchCalendarsPage({ lastDoc = null, filters }) {
  try {
    const constraints = buildQueryConstraints(filters, lastDoc);
    const calendarQuery = query(collection(db, "calendars"), ...constraints);

    const snapshot = await getDocs(calendarQuery);

    const calendars = snapshot.docs.map((doc) => new Calendar({ ...doc.data(), id: doc.id }));

    let totalCount = null;
    if (!lastDoc) {
      const countQuery = query(collection(db, "calendars"), ...buildCountQueryConstraints(filters));
      const countSnapshot = await getCountFromServer(countQuery);
      totalCount = countSnapshot.data().count;
    }

    return {
      calendars,
      lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
      hasMore: snapshot.docs.length === MAX_CALENDARS_LIMIT,
      totalCount,
    };
  } catch (error) {
    console.error("Failed to fetch calendars for table", error);
    throw error;
  }
}

/**
 * Hook that returns a function to invalidate the query and cause it to refetch.
 */
export const useInvalidateCalendarsForTables = () => {
  const queryClient = useQueryClient();
  return (filters) => invalidateQuery({ queryClient, queryKey: QUERY_KEY, filters });
};
